import RouteCard, { RouteStop } from "Components/Cards/RouteCard";
import ContentContainer from "Components/Container/ContentContainer";
import { Button, Input, LabelHeader } from "Components/CustomComponents";
import React, { useEffect } from "react";
import { Container, Spinner } from "react-bootstrap";
import {
  Controller,
  FieldValues,
  useFieldArray,
  useForm,
} from "react-hook-form";
import styles from "./index.module.scss";
import { useCreateRouteMutation } from "Data/services/routesApi";
import { ErrorToast, SuccessToast } from "Hooks/useToast";
import { getErrorMessage } from "Hooks/useErrorHandler";
import { useNavigate } from "react-router-dom";

export interface RouteForm {
  route_name: string;
  route_stops: RouteStop[];
}

const CreateRoute: React.FC = () => {
  const {
    control,
    handleSubmit,
    register,
    setValue,
    formState: { errors },
  } = useForm<RouteForm | FieldValues>({
    defaultValues: {
      route_name: "",
      route_stops: [], // Array to hold multiple stops
    },
  });

  // console.log(errors);
  const navigate = useNavigate();

  useEffect(() => {
    if (errors.route_name?.message) {
      ErrorToast(errors.route_name.message as string);
    }
  }, [errors.route_name?.message, errors.route_stops?.message]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: "route_stops",
  });

  const addStop = (isOneTime = false) => {
    append({
      customer_name: "",
      customer_location: "",
      latitude: null,
      longitude: null,
      phone_number: "",
      customer_notes: "",
      is_one_time_stop: isOneTime ? 1 : undefined, // Include only when true
      priority: fields.length + 1,
    });
  };

  const [createRoute, { isLoading }] = useCreateRouteMutation();

  const validateRouteStops = (routeStops: RouteStop[]): string | null => {
    if (routeStops.length < 2) {
      return "At least two stops are required.";
    }

    for (let i = 0; i < routeStops.length; i++) {
      const stop = routeStops[i];

      if (!stop.customer_name.trim()) {
        return `Customer name is required for stop ${i + 1}.`;
      }

      if (!stop.customer_location.trim()) {
        return `Customer location is required for stop ${i + 1}.`;
      }

      if (isNaN(parseFloat(stop.latitude))) {
        return `Latitude is required for stop ${i + 1}.`;
      }

      if (isNaN(parseFloat(stop.longitude))) {
        return `Longitude is required for stop ${i + 1}.`;
      }

      if (!/^\d+$/.test(stop.phone_number)) {
        return `Valid phone number is required for stop ${i + 1}.`;
      }
    }

    return null; // No errors
  };

  const onSubmit = async (data: RouteForm | FieldValues) => {
    // console.log("Final Payload:", data);
    const validationError = validateRouteStops(data.route_stops);

    if (validationError) {
      // console.log(validationError);
      ErrorToast(validationError);
      return;
    }

    try {
      const response = await createRoute(data).unwrap();

      if (response.code !== 200) {
        ErrorToast(response.message);
      }

      if (response.code === 200) {
        navigate(-1);
        SuccessToast(response.message);
      }
    } catch (error: unknown) {
      // console.log("Login Error:", error);
      ErrorToast(getErrorMessage(error));
    }
  };

  return (
    <ContentContainer classes={styles.CreateRoute}>
      <Container className="mx-0">
        <LabelHeader text={"Select Route No."} classes="mb-2" />
        <Controller
          name="route_name"
          control={control}
          rules={{
            required: "Route number is required.",
          }}
          render={({ field: { onChange, value } }) => (
            <Input
              outlined={true}
              placeholder="Route 1"
              onChange={onChange}
              value={value}
            />
          )}
        />

        <div className="d-flex flex-column gap-3 my-4">
          {fields.map((item, index) => {
            return (
              <RouteCard
                key={item.id}
                index={index}
                register={register}
                control={control}
                remove={remove}
                stopData={item as RouteStop}
                setValue={setValue}
              // oneTime={stop.oneTime}
              // count={index + 1}
              />
            );
          })}
        </div>
        <div
          className={`${styles.buttonContainer} d-flex flex-md-row flex-column align-items-center justify-content-between gap-4`}
        >
          <div className="w-100 d-flex flex-sm-row flex-column align-items-center gap-2">
            <Button
              text={"Add New Stop"}
              onClick={() => addStop(false)}
              classes={`${styles.button} darkBlueBg `}
            />
            <Button
              text={"Add One Time Stop"}
              onClick={() => addStop(true)}
              classes={`${styles.button} transparentBtn`}
            />
          </div>
          <Button
            text={
              isLoading ? (
                <Spinner animation="border" variant="light" size="sm" />
              ) : (
                "Continue"
              )
            }
            onClick={handleSubmit(onSubmit)}
            classes={`${styles.button} ${styles.submit} royalBlueBg`}
          />
        </div>
      </Container>
    </ContentContainer>
  );
};

export default CreateRoute;
